import * as React from 'react';
import * as echarts from 'echarts/core';
import { BarChart, LineChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import type { OrdersMonthlyRow } from './ordersValidationApi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LegendComponent,
  BarChart,
  LineChart,
  LabelLayout,
  UniversalTransition,
  SVGRenderer,
]);

export type OrdersChartVolumeMetric = 'orders' | 'mt';

const formatMtAxis = (value: number) =>
  new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(value);

const OrdersMonthlyTrendChart: React.FC<{
  data: OrdersMonthlyRow[];
  volumeMetric: OrdersChartVolumeMetric;
}> = ({ data, volumeMetric }) => {
  const chartRef = React.useRef<HTMLDivElement>(null);
  const chartInstance = React.useRef<echarts.ECharts | null>(null);

  React.useEffect(() => {
    if (!chartRef.current) return;
    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }
    const periods = data.map((row) => row.period);
    const volumeLabel = volumeMetric === 'mt' ? 'Quantity (MT)' : 'Orders';
    const volumeAxisName = volumeMetric === 'mt' ? 'MT' : 'Orders';
    const volumeData =
      volumeMetric === 'mt'
        ? data.map((row) => row.qty_mt)
        : data.map((row) => row.order_count);

    chartInstance.current.setOption({
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params];
          const month = items[0]?.axisValue ?? '';
          const lines = items.map((item: any) => {
            if (item.seriesName === volumeLabel) {
              const formatted =
                volumeMetric === 'mt'
                  ? `${formatMtAxis(item.data)} MT`
                  : Number(item.data).toLocaleString('en-IN');
              return `${item.marker}${item.seriesName}: ${formatted}`;
            }
            if (item.seriesName === 'Gross (₹ Cr)') {
              return `${item.marker}${item.seriesName}: ₹${Number(item.data).toLocaleString('en-IN', {
                maximumFractionDigits: 2,
              })} Cr`;
            }
            return `${item.marker}${item.seriesName}: ${item.data}`;
          });
          return [month, ...lines].join('<br/>');
        },
      },
      legend: { data: [volumeLabel, 'Gross (₹ Cr)'] },
      grid: { left: 56, right: 48, top: 40, bottom: 48 },
      xAxis: { type: 'category', data: periods, name: 'Month' },
      yAxis: [
        { type: 'value', name: volumeAxisName },
        { type: 'value', name: '₹ Cr', splitLine: { show: false } },
      ],
      series: [
        {
          name: volumeLabel,
          type: 'bar',
          data: volumeData,
          itemStyle: { color: '#1976d2' },
        },
        {
          name: 'Gross (₹ Cr)',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          data: data.map((row) => row.gross_total / 1e7),
          itemStyle: { color: '#ed6c02' },
        },
      ],
    });
    const onResize = () => chartInstance.current?.resize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [data, volumeMetric]);

  return <div ref={chartRef} style={{ width: '100%', height: 360 }} />;
};

export default OrdersMonthlyTrendChart;
